import { VisuallyHidden } from "./VisuallyHidden";
import { describeCountdown, formatCountdown } from "../lib/countdown";
import styles from "./CountdownDisplay.module.css";
import type { Countdown } from "../lib/countdown";

/**
 * A ticking countdown that assistive technology hears once.
 *
 * The digits are `aria-hidden`: a per-second live region would talk over a screen-reader
 * user continuously. The accessible text instead gives the remaining time in words plus the
 * absolute UTC instant, which is the durable fact — canon § 8 keeps canonical time in UTC
 * while allowing the UI to show a countdown.
 *
 * Past the boundary the component stops implying time is left: no digits, only the closed
 * message. Canon `AGENTS.md` § 11 forbids fake urgency, so there is no negative timer,
 * nothing that keeps counting after the event, and no colour change as the hour approaches.
 */
export function CountdownDisplay({
  label,
  countdown,
  absolute,
  expiredLabel,
}: {
  /** What is being counted down to, e.g. "Bidding closes in". */
  label?: string | undefined;
  countdown: Countdown;
  /** The same instant in plain words, e.g. "00:00 UTC on Tuesday 25 August 2026". */
  absolute: string;
  /** Shown instead of the digits once the instant has passed. */
  expiredLabel: string;
}) {
  if (countdown.expired) {
    return (
      <p className={styles.countdown}>
        <span className={styles.label}>{expiredLabel}</span>
      </p>
    );
  }

  return (
    <p className={styles.countdown}>
      {label === undefined ? null : <span className={styles.label}>{label}</span>}
      <span className={`${styles.value} figure`} aria-hidden="true">
        {formatCountdown(countdown)}
      </span>
      <VisuallyHidden>
        {describeCountdown(countdown)} remaining, at {absolute}.
      </VisuallyHidden>
    </p>
  );
}
